import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import HiredLogo from "@/components/brand/HiredLogo";
import { TRIAL_CTA_SUBTEXT } from "@/components/trial/TrialCTA";

export default function Signup() {
  const nav = useNavigate();
  const loc = useLocation();
  const q = new URLSearchParams(loc.search);
  const initialEmployee = q.get("employee") || "hoa-steward";

  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ email: "", employee_slug: initialEmployee });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/employees").then(({ data }) => setEmployees(data.filter(e => e.status !== "Coming Soon")))
       .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) { toast.error("Enter your email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/signup-magic-link", {
        email: form.email.trim(),
        employee_slug: form.employee_slug,
        origin_url: window.location.origin,
      });
      // dev-safe: if we got a dev_link back (email service not configured), auto-persist for the CheckEmail page
      sessionStorage.setItem("hired_pending_email", form.email.trim());
      sessionStorage.setItem("hired_pending_employee", form.employee_slug);
      if (data?.dev_link) sessionStorage.setItem("hired_dev_link", data.dev_link);
      nav("/check-email");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send link. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-obsidian text-ivory flex">
      <div className="hidden md:block flex-1 border-r border-line p-14">
        <Link to="/"><HiredLogo size={22} /></Link>
        <div className="mt-24 max-w-lg">
          <div className="brand-eyebrow mb-4">FREE TRIAL</div>
          <div className="brand-headline text-6xl">14 days free.<br /><span className="text-purple italic">Zero card.</span></div>
          <div className="text-steel mt-8 text-lg leading-relaxed">
            Pick your first AI employee and get started in minutes. We&apos;ll send a secure link to your email — no password needed.
          </div>
          <ul className="mt-10 space-y-3 text-sm text-ivory/90">
            <li className="flex items-start gap-3"><span className="dot dot-green mt-1.5" /> No credit card required</li>
            <li className="flex items-start gap-3"><span className="dot dot-green mt-1.5" /> 1 AI employee · 1 tool connection · up to 25 tasks</li>
            <li className="flex items-start gap-3"><span className="dot dot-green mt-1.5" /> Cancel any time — your work stays</li>
          </ul>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="signup-form">
          <div className="brand-eyebrow mb-6">CREATE ACCOUNT</div>
          <h1 className="brand-headline text-5xl mb-3">Start your 14-day free trial</h1>
          <p className="text-steel text-base mb-10">No credit card required. Pick your first AI employee and get started in minutes.</p>
          <div className="space-y-5">
            <div>
              <label htmlFor="signup-email">Email</label>
              <input data-testid="signup-email" id="signup-email" type="email" required
                     value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                     placeholder="you@company.com" />
            </div>
            <div>
              <label htmlFor="signup-employee">Choose your AI employee</label>
              <select data-testid="signup-employee" id="signup-employee"
                      value={form.employee_slug}
                      onChange={e => setForm({...form, employee_slug: e.target.value})}>
                {employees.map(e => (
                  <option key={e.slug} value={e.slug}>{e.name} — {e.industry}</option>
                ))}
              </select>
            </div>
          </div>
          <button data-testid="signup-submit-btn" type="submit" disabled={busy}
                  className="btn-gold w-full justify-center mt-8">
            {busy ? <><Loader2 size={16} className="animate-spin" /> Sending…</>
                  : <>Start Free — No Card Required <ArrowRight size={16} /></>}
          </button>
          <div data-testid="signup-sub" className="mt-3 text-xs text-steel tracking-wide">{TRIAL_CTA_SUBTEXT}</div>
          <div className="mt-8 text-steel text-sm">
            We&apos;ll send a secure link to your email — no password needed.
          </div>
          <div className="mt-8 text-steel text-sm">
            Have an account? <Link to="/login" className="text-gold hover:underline">Log in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
